import { Component, computed, effect, input, signal } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TNil } from '@app/app.interface';
import { ICtaForm } from '@campaign-form/campaign-form.interface';
import { EExecutionType } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { ExecutionExpectedKpisService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/execution-expected-kpis.service';
import { EPlatformFeature } from '@enums/campaign.enum';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { MatErrorComponent } from '@shared/components/mat-error/mat-error.component';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { Subscription } from 'rxjs';
import { IReviewExecution, IReviewExpectedKpi, IScopedItem, TReviewDeliverable } from './review-step.interface';

@Component({
  imports: [
    IconComponent,
    MatErrorComponent,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    SocialBadgeComponent,
    TranslateModule,
    MatTooltipModule,
  ],
  selector: 'app-review-step',
  templateUrl: './review-step.component.html',
})
export class ReviewStepComponent {
  public readonly ctaForm = input.required<FormGroup<ICtaForm> | null>();
  public readonly customModeCreatorId = input<string | TNil>(null);
  public readonly selectedPlatformFeature = input.required<EPlatformFeature | null>();

  private readonly formValueVersion = signal<number>(0);

  public readonly ctaFormGroup = computed((): FormGroup<ICtaForm> | null => this.ctaForm());
  public readonly deliverableCount = computed((): number => {
    this.formValueVersion();

    return this.getScopedDeliverables().length;
  });
  public readonly deliverableLabelTranslationKey = computed((): string => {
    if (this.deliverableCount() === 1) {
      return 'pages.campaignForm.sections.creatorsAndCtas.deliverable';
    }

    return 'pages.campaignForm.sections.creatorsAndCtas.deliverables';
  });
  public readonly executionCount = computed((): number => {
    this.formValueVersion();

    return this.getScopedExecutions().reduce((count: number, execution: IReviewExecution) => {
      if (execution.executionType === EExecutionType.always) {
        return count;
      }

      if (execution.executionType === EExecutionType.unique) {
        return count + 1;
      }

      if (execution.executionType === EExecutionType.undesignated) {
        return count + Number(execution.executionCountTarget ?? 1);
      }

      return count;
    }, 0);
  });
  public readonly isCustomMode = computed((): boolean => !!this.customModeCreatorId());
  public readonly kpiCount = computed((): number => {
    this.formValueVersion();

    return this.getScopedExecutions().reduce((count: number, execution: IReviewExecution) => {
      const expectedKpis: IReviewExpectedKpi[] = execution.expectedKpis ?? [];

      return (
        count +
        expectedKpis.filter((expectedKpi: IReviewExpectedKpi) => {
          return (
            ExecutionExpectedKpisService.isExpectedKpiVisible(
              expectedKpi,
              this.customModeCreatorId(),
              this.isCustomMode(),
              false,
            ) && ExecutionExpectedKpisService.isExpectedValueSet(expectedKpi.expectedValue)
          );
        }).length
      );
    }, 0);
  });
  public readonly kpiLabelTranslationKey = computed((): string => {
    if (this.kpiCount() === 1) {
      return 'pages.campaignForm.sections.creatorsAndCtas.kpi';
    }

    return 'pages.campaignForm.sections.creatorsAndCtas.kpis';
  });

  constructor() {
    effect((onCleanup) => {
      const ctaForm: FormGroup<ICtaForm> | null = this.ctaForm();
      let subscription: Subscription | null = null;

      if (ctaForm) {
        subscription = ctaForm.valueChanges.subscribe(() => {
          this.formValueVersion.update((version: number) => version + 1);
        });
      }

      onCleanup(() => {
        subscription?.unsubscribe();
      });
    });
  }

  private getScopedDeliverables(): TReviewDeliverable[] {
    const ctaForm: FormGroup<ICtaForm> | null = this.ctaForm();

    if (!ctaForm) {
      return [];
    }

    return (ctaForm.getRawValue().deliverables ?? []).filter((deliverable) => {
      return this.isVisibleInCurrentScope(deliverable);
    });
  }

  private getScopedExecutions(): IReviewExecution[] {
    const ctaForm: FormGroup<ICtaForm> | null = this.ctaForm();

    if (!ctaForm) {
      return [];
    }

    return (ctaForm.getRawValue().executions ?? []).filter((execution) => {
      return this.isVisibleInCurrentScope(execution);
    });
  }

  private isVisibleInCurrentScope(item: IScopedItem): boolean {
    const creatorId: string | TNil = this.customModeCreatorId();

    if (!this.isCustomMode()) {
      return !item.exclusiveTo;
    }

    return !item.exclusiveTo || item.exclusiveTo === creatorId;
  }
}
